import { DEFAULT_API_URL } from "./const";
import type { UserRole } from "@textformatter/shared";

const API_URL = import.meta.env.VITE_API_URL || DEFAULT_API_URL;

export type AuthUser = {
	id: string;
	email: string;
	role: UserRole;
	hasPasswordAuth: boolean;
	hasGoogleAuth: boolean;
};

async function request<T>(path: string, options: RequestInit = {}) {
	const mergedHeaders = new Headers(options.headers);
	const hasBody = options.body !== undefined && options.body !== null;
	if (hasBody && !mergedHeaders.has("Content-Type")) {
		mergedHeaders.set("Content-Type", "application/json");
	}

	const res = await fetch(`${API_URL}${path}`, {
		credentials: "include",
		headers: mergedHeaders,
		...options,
	});
	const data = await res.json().catch(() => ({}));
	if (!res.ok) {
		const message = data?.error || "Request failed";
		throw new Error(message);
	}
	return data as T;
}

export const api = {
	register: (email: string, password: string) =>
		request<{ ok: true; needsEmailConfirmation: true }>("/register", {
			method: "POST",
			body: JSON.stringify({ email, password }),
		}),
	login: (email: string, password: string) =>
		request<{ user: { id: string; email: string; role: UserRole } }>("/login", {
			method: "POST",
			body: JSON.stringify({ email, password }),
		}),
	forgotPassword: (email: string) =>
		request<{ ok: true }>("/forgot-password", {
			method: "POST",
			body: JSON.stringify({ email }),
		}),
	resetPassword: (token: string, password: string) =>
		request<{ ok: true }>("/reset-password", {
			method: "POST",
			body: JSON.stringify({ token, password }),
		}),
	resendConfirmation: (email: string) =>
		request<{ ok: true }>("/resend-confirmation", {
			method: "POST",
			body: JSON.stringify({ email }),
		}),
	confirmEmail: (token: string) =>
		request<{ user: { id: string; email: string; role: UserRole } }>("/confirm-email", {
			method: "POST",
			body: JSON.stringify({ token }),
		}),
	changePassword: (payload: { currentPassword?: string; newPassword: string }) =>
		request<{ ok: true }>("/change-password", {
			method: "POST",
			body: JSON.stringify(payload),
		}),
	logout: () => request<{ ok: boolean }>("/logout", { method: "POST" }),
	me: () => request<{ user: AuthUser | null }>("/me"),
	listApiKeys: () => request<{ keys: ApiKey[] }>("/api-keys"),
	getApiKey: (id: string) => request<{ key: ApiKey }>(`/api-keys/${encodeURIComponent(id)}`),
	revealApiKey: (id: string, password?: string) =>
		request<{ value: string }>(`/api-keys/${encodeURIComponent(id)}/reveal`, {
			method: "POST",
			body: JSON.stringify(password ? { password } : {}),
		}),
	createApiKey: (payload: { label?: string; words: number }) =>
		request<{ key: ApiKey; value: string }>("/api-keys", {
			method: "POST",
			body: JSON.stringify(payload),
		}),
	deleteApiKey: (id: string) =>
		request<{ ok: boolean }>(`/api-keys/${encodeURIComponent(id)}`, { method: "DELETE" }),
	listUsersAsAdmin: (params?: { page?: number; limit?: number }) => {
		const search = new URLSearchParams();
		if (typeof params?.page === "number") {
			search.set("page", String(params.page));
		}
		if (typeof params?.limit === "number") {
			search.set("limit", String(params.limit));
		}
		const suffix = search.toString();
		return request<{ users: AdminUser[]; pagination: PaginationMeta }>(
			`/admin/users${suffix ? `?${suffix}` : ""}`,
		);
	},
	listUserApiKeysAsAdmin: (userId: string) =>
		request<{ keys: ApiKey[] }>(`/admin/users/${encodeURIComponent(userId)}/api-keys`),
	getApiKeyAsAdmin: (id: string) =>
		request<{ key: ApiKey }>(`/admin/api-keys/${encodeURIComponent(id)}`),
	createCheckoutSession: (payload: { label?: string; words: number }) =>
		request<{ id: string; url: string }>("/payments/checkout-session", {
			method: "POST",
			body: JSON.stringify(payload),
		}),
	confirmCheckoutSession: (sessionId: string) =>
		request<{ key?: ApiKey; status?: "pending"; value?: string }>(
			`/payments/checkout-session/${sessionId}`,
		),
};

export type ApiKey = {
	id: string;
	label: string;
	keyPreview?: string | null;
	keyLast4?: string | null;
	words: number;
	wordsUsed: number;
	price: number;
	lastUsedAt?: string | null;
	createdAt?: string;
};

export type AdminUser = {
	id: string;
	email: string;
	role: UserRole;
	hasPasswordAuth: boolean;
	hasGoogleAuth: boolean;
	createdAt: string;
};

export type PaginationMeta = {
	page: number;
	limit: number;
	total: number;
	totalPages: number;
};
